/**
 * Paiements: the finance console (WALLIYO-0035). The contract for the
 * console's payment screens.
 *
 * - **Walliyo only takes money from pros**: platform fees per introduction and
 *   monthly subscriptions, paid by Orange Money or card (Stripe). It never
 *   holds job money (ADR-0008).
 * - **Four corrections**, each recording who, the amount, the reason and a
 *   provider reference: a **refund** (money back through the provider), a
 *   **credit** (a balance applied to the pro's next fee), a **waiver** (a fee
 *   cancelled with a reason) and **mark as paid** (which needs the provider's
 *   transaction reference, so a manual correction always ties to a real
 *   payment).
 * - **Without its permission, or above the issuer's limit** (ADR-0019), a
 *   refund, a credit or a waiver becomes an approval request. Whoever
 *   approves could have done it themselves, and nobody approves their own
 *   request.
 * - **No money action is retried automatically** (ADR-0016 §2.1). A refund
 *   through a provider is saved `pending`, sent once after it is saved, and
 *   settled once. A timeout leaves it `pending` with `outcomeUnknown`: the
 *   screen says "status unknown, checking", never resends.
 *
 * Every write names the `version` the screen was showing (ADR-0016 §2.3) and
 * MUST carry an `Idempotency-Key` header: money writes refuse to run without
 * one. Every endpoint answers under its own response name (WALLIYO-0068).
 */

// ── Vocabulary ───────────────────────────────────────────────────────────

/** The providers a pro pays Walliyo through. Wave is wired but not offered yet. */
export type PaymentProvider = "orange_money" | "lengo_pay" | "stripe" | "wave";

/** A fee payment's status. `waived` is set only by the console. */
export type FeePaymentStatus = "pending" | "paid" | "failed" | "refunded" | "waived";

/** What the fee pays for: one introduction, or a month or a year of subscription. */
export type FeeType = "per_job" | "monthly" | "yearly";

export type PaymentAdjustmentKind = "refund" | "credit" | "waive" | "mark_paid";

/**
 * - `pending`: saved, to be sent to the provider once (a refund through Stripe).
 * - `awaiting_manual_transfer`: Finance sends the money by hand (Orange Money
 *   has no refund API), then settles it with the transfer's reference.
 * - `awaiting_approval`: above the limit, waiting for the Owner.
 * - `done`, `failed`, `rejected`: final.
 */
export type PaymentAdjustmentStatus =
  | "pending"
  | "done"
  | "failed"
  | "awaiting_manual_transfer"
  | "awaiting_approval"
  | "rejected";

export type ApprovalRequestStatus = "pending" | "approved" | "rejected";

export const PAYMENT_PROVIDERS: readonly PaymentProvider[] = ["orange_money", "lengo_pay", "stripe", "wave"];
export const FEE_PAYMENT_STATUSES: readonly FeePaymentStatus[] = ["pending", "paid", "failed", "refunded", "waived"];
export const APPROVAL_REQUEST_STATUSES: readonly ApprovalRequestStatus[] = ["pending", "approved", "rejected"];

export const PAYMENTS_DEFAULT_LIMIT = 50;
export const PAYMENTS_MAX_LIMIT = 200;

/**
 * Two paid fees from the same pro, for the same amount, this close together
 * are flagged as a likely double charge: on a patchy network the pro
 * confirms, the network drops, they retry, and both go through.
 */
export const LIKELY_DUPLICATE_WINDOW_MINUTES = 10;

// ── Payments ─────────────────────────────────────────────────────────────

export type PaymentPro = {
  workerId: string;
  userId: string;
  name: string;
  /** e.g. "+224 6•• •• •• 41". Revealing it is its own action, on the pro's page. */
  maskedPhone: string | null;
};

export type FeePaymentListItem = {
  platformFeePaymentId: string;
  pro: PaymentPro;
  feeType: FeeType;
  jobId: string | null;
  /** In the currency's major unit: 25000 GNF, 2.00 USD. */
  amount: number;
  currency: string;
  provider: PaymentProvider;
  /** The provider's id for the payment: an Orange Money pay token, a Stripe id. */
  providerTransactionId: string | null;
  status: FeePaymentStatus;
  /** Refunds done so far. A partial refund leaves the status `paid`. */
  refundedAmount: number;
  /** Another paid fee from the same pro, same amount, within the window. */
  likelyDuplicate: boolean;
  /** The other payments it looks like a duplicate of. */
  duplicateOf: string[];
  createdAt: string;
  version: number;
};

export type PaymentAdjustment = {
  paymentAdjustmentId: string;
  kind: PaymentAdjustmentKind;
  platformFeePaymentId: string | null;
  workerId: string;
  amount: number;
  currency: string;
  reason: string;
  provider: PaymentProvider | null;
  /** The refund's or transfer's id at the provider; the payment's for mark-paid. */
  providerReference: string | null;
  status: PaymentAdjustmentStatus;
  /** A refund sent to the provider with no answer yet: "status unknown, checking". Never resent. */
  outcomeUnknown: boolean;
  failureReason: string | null;
  staff: { staffMemberId: string; name: string };
  approvalRequestId: string | null;
  providerAttemptedAt: string | null;
  settledAt: string | null;
  settledBy: { staffMemberId: string; name: string } | null;
  createdAt: string;
  updatedAt: string;
  version: number;
};

export type FeePaymentDetail = FeePaymentListItem & {
  adjustments: PaymentAdjustment[];
  /** What can still be refunded: paid, less refunds done, pending or awaiting approval. */
  refundableAmount: number;
  /** The pro's unspent credit in this payment's currency. */
  proCreditBalance: number;
};

/** GET /api/v1/console/payments (VIEW_PAYMENTS). Newest first. */
export type ListPaymentsQuery = {
  workerId?: string;
  provider?: PaymentProvider;
  status?: FeePaymentStatus;
  /** ISO date or date-time; `to` date-only means the whole of that day. */
  from?: string;
  to?: string;
  /** Only payments flagged as likely double charges. */
  likelyDuplicates?: boolean;
  cursor?: string;
  limit?: number;
};
export type ListPaymentsResponse = { payments: FeePaymentListItem[]; nextCursor: string | null };

/** GET /api/v1/console/payments/:platformFeePaymentId (VIEW_PAYMENTS). */
export type GetPaymentResponse = { payment: FeePaymentDetail };

// ── The four corrections ─────────────────────────────────────────────────

/**
 * What a correction did:
 * - `done`: applied now (a waiver, mark-paid, a credit, or a refund the provider confirmed);
 * - `pending`: sent to the provider, no answer yet: show "status unknown, checking";
 * - `awaiting_manual_transfer`: Finance must send it by hand, then settle it;
 * - `awaiting_approval`: above the limit, the Owner decides;
 * - `failed`: the provider refused; nothing moved. Start a new refund if it should be tried again.
 */
export type PaymentActionResponse = {
  payment: FeePaymentDetail | null;
  adjustment: PaymentAdjustment;
  approvalRequest: ApprovalRequest | null;
};

/** POST /api/v1/console/payments/:platformFeePaymentId/refunds (ISSUE_REFUND). Omit amount for the whole refundable amount. */
export type IssueRefundRequest = { amount?: number; reason: string; version: number };
export type IssueRefundResponse = PaymentActionResponse;

/** POST /api/v1/console/payments/:platformFeePaymentId/waive (WAIVE_FEE): a pending or failed fee. */
export type WaiveFeeRequest = { reason: string; version: number };
export type WaiveFeeResponse = PaymentActionResponse;

/**
 * POST /api/v1/console/payments/:platformFeePaymentId/mark-paid (MARK_PAYMENT_PAID).
 * `providerReference` is the real payment's transaction reference (the Orange
 * Money transaction id from the pro's SMS or the statement). One reference
 * marks one fee paid, never two.
 */
export type MarkPaymentPaidRequest = { providerReference: string; reason: string; version: number };
export type MarkPaymentPaidResponse = PaymentActionResponse;

/**
 * POST /api/v1/console/payments/credits (ISSUE_CREDIT): a balance applied to
 * the pro's next fees in that currency. Name the fee it compensates, if any,
 * with the version the screen showed.
 */
export type IssueCreditRequest = {
  workerId: string;
  amount: number;
  currency: string;
  reason: string;
  platformFeePaymentId?: string | null;
  version?: number | null;
};
export type IssueCreditResponse = PaymentActionResponse;

/**
 * POST /api/v1/console/payments/adjustments/:paymentAdjustmentId/settle (SETTLE_REFUND):
 * Finance records how a `pending` or `awaiting_manual_transfer` refund ended.
 * `done` needs the transfer's or refund's reference at the provider.
 */
export type SettleRefundRequest = {
  outcome: "done" | "failed";
  providerReference?: string | null;
  reason: string;
  version: number;
};
export type SettleRefundResponse = PaymentActionResponse;

// ── Approval requests ────────────────────────────────────────────────────

export type ApprovalRequest = {
  approvalRequestId: string;
  /** The action waiting: ISSUE_REFUND_STAFF_ACTION, ISSUE_CREDIT_STAFF_ACTION or WAIVE_FEE_STAFF_ACTION. */
  actionId: string;
  status: ApprovalRequestStatus;
  subjectUserId: string | null;
  targetType: string;
  targetId: string;
  /** What will run if approved, e.g. { kind, amount, currency, platformFeePaymentId, paymentAdjustmentId }. */
  payload: Record<string, unknown>;
  /**
   * The permission the request needed (ADR-0019 §6), whether the requester
   * held it, and their limit in the request's currency (`payload.currency`)
   * when they asked. Null with `heldAtRequest`: they hold it, with no limit
   * in that currency.
   */
  permissionId: string;
  heldAtRequest: boolean;
  limitAtRequest: number | null;
  requestedBy: { staffMemberId: string; name: string };
  requestReason: string;
  decidedBy: { staffMemberId: string; name: string } | null;
  decisionReason: string | null;
  decidedAt: string | null;
  createdAt: string;
  version: number;
};

/** GET /api/v1/console/approval-requests (VIEW_PAYMENTS or APPROVE_PAYMENT_REQUESTS). Oldest pending first. */
export type ListApprovalRequestsQuery = { status?: ApprovalRequestStatus };
export type ListApprovalRequestsResponse = { approvalRequests: ApprovalRequest[] };

/** POST /api/v1/console/approval-requests/:approvalRequestId/approve (APPROVE_PAYMENT_REQUEST, Owner). */
export type ApprovePaymentRequestRequest = { reason: string; version: number };
export type ApprovePaymentRequestResponse = PaymentActionResponse;

/** POST /api/v1/console/approval-requests/:approvalRequestId/reject (REJECT_PAYMENT_REQUEST, Owner). */
export type RejectPaymentRequestRequest = { reason: string; version: number };
export type RejectPaymentRequestResponse = PaymentActionResponse;

// ── Export ───────────────────────────────────────────────────────────────

/**
 * GET /api/v1/console/payments/export?month=2026-09 (EXPORT_PAYMENTS): a CSV
 * of the month's fee payments (UTC), audited as a sensitive read. It holds
 * what Finance reconciles with, and no contact details: the pro's name and
 * ids, never a phone number or email.
 */
export const PAYMENTS_EXPORT_COLUMNS = [
  "platform_fee_payment_id",
  "created_at_utc",
  "pro_worker_id",
  "pro_name",
  "fee_type",
  "job_id",
  "provider",
  "provider_transaction_id",
  "amount",
  "currency",
  "status",
  "refunded_amount",
  "waived",
  "marked_paid_reference",
  "likely_duplicate",
] as const;
